// Phase 10 — AcademyPage.
//
// STRIKZA Academy: 8 educational courses covering probability,
// CLV, risk management, and platform methodology. Read-only
// educational content, NOT betting advice.

import { useMemo } from "react";
import type { CSSProperties } from "react";
import { useAcademy, ACADEMY_COURSES } from "../../knowledge/useAcademy.ts";
import type { AcademyCourse } from "../../knowledge/useAcademy.ts";
import { cardLow, cardMedium } from "../../styles/elevation.ts";
import { transitionStandard } from "../../styles/motion.ts";

// ─── Styles ───────────────────────────────────────────────────

const root: CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: "var(--sz-space-4)",
};

const header: CSSProperties = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "baseline",
};

const title: CSSProperties = {
  fontFamily: "var(--sz-font-display)",
  fontSize: "clamp(1.6rem, 4vw, 2.4rem)",
  letterSpacing: "3px",
  textTransform: "uppercase",
  color: "var(--sz-text-primary)",
  lineHeight: 1,
};

const subtitle: CSSProperties = {
  fontFamily: "var(--sz-font-mono)",
  fontSize: "var(--sz-mono-xs)",
  letterSpacing: "2px",
  textTransform: "uppercase",
  color: "var(--sz-text-muted)",
  marginTop: "4px",
};

const disclosure: CSSProperties = {
  fontFamily: "var(--sz-font-mono)",
  fontSize: "var(--sz-mono-xs)",
  letterSpacing: "0.5px",
  color: "var(--sz-text-muted)",
  padding: "var(--sz-space-2) var(--sz-space-3)",
  borderRadius: "var(--sz-radius-sm)",
  background: "var(--sz-surface-recessed)",
  lineHeight: 1.5,
};

const courseGrid: CSSProperties = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))",
  gap: "var(--sz-space-3)",
};

const courseCard: CSSProperties = {
  ...cardMedium,
  ...transitionStandard,
  display: "flex",
  flexDirection: "column",
  gap: "var(--sz-space-2)",
  padding: "var(--sz-space-4)",
  borderRadius: "var(--sz-radius-md)",
  background: "var(--sz-surface-card)",
  borderTop: "3px solid var(--sz-amber)",
  cursor: "pointer",
};

const courseTitle: CSSProperties = {
  fontFamily: "var(--sz-font-display)",
  fontSize: "1.2rem",
  letterSpacing: "1px",
  textTransform: "uppercase",
  color: "var(--sz-text-primary)",
  lineHeight: 1.2,
};

const courseDesc: CSSProperties = {
  fontFamily: "var(--sz-font-body)",
  fontSize: "var(--sz-body-sm)",
  color: "var(--sz-text-secondary)",
  lineHeight: 1.6,
};

const courseMeta: CSSProperties = {
  display: "flex",
  gap: "var(--sz-space-2)",
  flexWrap: "wrap",
  alignItems: "center",
  marginTop: "var(--sz-space-1)",
};

const metaPill: CSSProperties = {
  fontFamily: "var(--sz-font-mono)",
  fontSize: "9px",
  letterSpacing: "1px",
  textTransform: "uppercase",
  padding: "2px 8px",
  borderRadius: "var(--sz-radius-sm)",
  background: "var(--sz-surface-recessed)",
  color: "var(--sz-text-muted)",
};

const categoryPill: CSSProperties = {
  ...metaPill,
  background: "rgba(245, 158, 11, 0.12)",
  color: "var(--sz-amber)",
};

const DIFFICULTY_STYLES: Record<string, CSSProperties> = {
  beginner: { ...metaPill, background: "rgba(34, 197, 94, 0.12)", color: "var(--sz-success-text)" },
  intermediate: { ...metaPill, background: "rgba(245, 158, 11, 0.12)", color: "var(--sz-amber)" },
  advanced: { ...metaPill, background: "rgba(239, 68, 68, 0.12)", color: "var(--sz-error-text)" },
};

const sectionLabel: CSSProperties = {
  fontFamily: "var(--sz-font-display)",
  fontSize: "1.1rem",
  letterSpacing: "2px",
  textTransform: "uppercase",
  color: "var(--sz-text-primary)",
};

const countLabel: CSSProperties = {
  fontFamily: "var(--sz-font-mono)",
  fontSize: "var(--sz-mono-xs)",
  letterSpacing: "1px",
  color: "var(--sz-text-muted)",
};

const phantomBadge: CSSProperties = {
  fontFamily: "var(--sz-font-mono)",
  fontSize: "9px",
  letterSpacing: "2px",
  textTransform: "uppercase",
  color: "var(--sz-text-muted)",
  opacity: 0.5,
  textAlign: "center",
  padding: "var(--sz-space-2) 0",
};

// ─── Course Card Component ────────────────────────────────────

function CourseCard({
  course,
}: {
  course: {
    title: string;
    description: string;
    category: string;
    lessons: number;
    mins: number;
    difficulty: string;
  };
}): JSX.Element {
  const diffStyle = DIFFICULTY_STYLES[course.difficulty] ?? DIFFICULTY_STYLES.beginner;

  return (
    <div style={courseCard}>
      <span style={courseTitle}>{course.title}</span>
      <span style={courseDesc}>{course.description}</span>
      <div style={courseMeta}>
        <span style={categoryPill}>{course.category}</span>
        <span style={diffStyle}>{course.difficulty}</span>
        <span style={metaPill}>{String(course.lessons)} lessons</span>
        <span style={metaPill}>{String(course.mins)} min</span>
      </div>
    </div>
  );
}

// ─── Page ─────────────────────────────────────────────────────

export function AcademyPage(): JSX.Element {
  const { courses: dbCourses } = useAcademy();

  // Use DB courses if available, otherwise show static definitions
  const displayCourses = useMemo(() => {
    if (dbCourses.length > 0) {
      return dbCourses.map((c) => ({
        title: c.title,
        description: c.description ?? "",
        category: c.category,
        lessons: c.lesson_count,
        mins: c.estimated_mins,
        difficulty: c.difficulty,
      }));
    }
    return ACADEMY_COURSES;
  }, [dbCourses]);

  // Group by category
  const grouped = useMemo(() => {
    const g = new Map<string, typeof displayCourses>();
    for (const c of displayCourses) {
      const existing = g.get(c.category) ?? [];
      existing.push(c);
      g.set(c.category, existing);
    }
    return g;
  }, [displayCourses]);

  return (
    <div style={root}>
      {/* Header */}
      <div style={header}>
        <div>
          <h2 style={title}>STRIKZA Academy</h2>
          <div style={subtitle}>
            8 courses · probability · risk management · platform mastery
          </div>
        </div>
        <span style={countLabel}>{String(displayCourses.length)} courses</span>
      </div>

      {/* Disclosure */}
      <div style={disclosure}>
        Academy content is general education covering probability, risk management,
        and platform methodology. Nothing here recommends a stake, a placement,
        or a specific outcome. Manual placement only.
      </div>

      {/* Courses by category */}
      {Array.from(grouped.entries()).map(([category, courses]) => (
        <div key={category}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
            <span style={sectionLabel}>{category}</span>
            <span style={countLabel}>{String(courses.length)} courses</span>
          </div>
          <div style={{ ...courseGrid, marginTop: "var(--sz-space-2)" }}>
            {courses.map((c) => (
              <CourseCard key={c.title} course={c} />
            ))}
          </div>
        </div>
      ))}

      <span style={phantomBadge}>Phantom · simulated</span>
    </div>
  );
}
